import { staleTimes, useQuery } from '@platform/api-client';
import { Button, formatMoney } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useState } from 'react';
import { nameIn } from '../catalog/categories.ts';
import { LoadFailed } from '../LoadFailed.tsx';
import { SearchField } from '../SearchField.tsx';
import { queryKeys, usePageContext } from '../usePageContext.ts';
import styles from './Calculator.module.css';

const RESULT_PAGE_SIZE = 5;

export type StartingPrices = { readonly price: string; readonly cost: string | null };

export type ProductStartProps = { api: TypedClient; onPick: (prices: StartingPrices) => void };

export const ProductStart = ({ api, onPick }: ProductStartProps) => {
  const { t, locale, tenantId, describeError } = usePageContext();
  const [term, setTerm] = useState<string | null>(null);
  const found = useQuery({
    queryKey: queryKeys.products(tenantId, { view: 'calculator', search: term }),
    queryFn: () =>
      api.get('/admin/products', {
        query: { search: term ?? '', status: 'active', page_size: RESULT_PAGE_SIZE },
      }),
    enabled: term !== null,
    staleTime: staleTimes.catalog,
  });
  const variants = (found.data?.items ?? []).flatMap((product) =>
    product.variants.map((variant) => ({ product, variant })),
  );
  return (
    <div className={styles['start']}>
      <SearchField
        label={t('calculator.productSearch')}
        buttonLabel={t('calculator.productSearchButton')}
        onSearch={setTerm}
      />
      {found.isError ? (
        <LoadFailed description={describeError(found.error)} onRetry={() => void found.refetch()} />
      ) : null}
      {found.data !== undefined && variants.length === 0 ? (
        <p className={styles['muted']}>{t('calculator.productNone')}</p>
      ) : null}
      {variants.length === 0 ? null : (
        <ul className={styles['picks']}>
          {variants.map(({ product, variant }) => (
            <li key={variant.id}>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  onPick({ price: variant.price.amount, cost: variant.costPrice?.amount ?? null });
                }}
              >
                {t('calculator.productPick', {
                  name: nameIn(product, locale, variant.sku),
                  sku: variant.sku,
                  price: formatMoney(variant.price, locale),
                })}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
